import Page from 'components/menu/page.component';
import GroupSetting from 'components/settings/groupSetting.component';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { dayjs, LoggerService, RootState, ShortUser, usePersistantState } from '@umanforge/common';
import { UserService } from 'services/users.service';
import { useSelector, useDispatch } from 'react-redux';
import { KEY } from 'constants/local_storage_key.constant';
import { ToastService } from 'services/toast.service';
import { PlusSVG } from '@common_components/svg/plus.svg';
import TimeGridTile from 'features/timegrid/components/timeGridTile.component';
import { ModifyTimeGrid, PostTimeGrid, TimeGrid } from 'interfaces/timegrid.interface';
import TimeGridCalendar from 'features/timegrid/components/timeGridCalendar.component';
import { TimeGridService } from 'services/timeGrid.service';
import { TimeGridRecurrenceItem } from 'interfaces/timegridRecurrence.interface';
import { TimeGridRecurrenceService } from 'services/timeGridRecurrence.service';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import TimeGridModal from 'features/timegrid/components/timeGridModal.component';
import { BreaktimeService } from 'services/breaktime.service';
import { PostBreaktime } from 'interfaces/breaktime.interface';
import { SettingService } from 'services/setting.service';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';

function TimeGridView() {
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const toastService = new ToastService( 'TimeGridView' );
  const logger: LoggerService = new LoggerService( 'TimeGrid page' );

  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ timegrids, setTimeGrids ] = useState<TimeGrid[]>( [] );
  const [ timegridRecurrences, setTimeGridRecurrences ] = useState<TimeGridRecurrenceItem[]>( [] );
  const [ users, setUsers ] = useState<ShortUser[]>( [] );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ isDeleteTimeGridRecurrenceModalOpen, setIsDeleteTimeGridRecurrenceModalOpen ] = useState<TimeGridRecurrenceItem | undefined>( undefined );
  const [ isDeleteTimeGridModalOpen, setIsDeleteTimeGridModalOpen ] = useState<TimeGrid | undefined>( undefined );

  const [ timegridRecurrenceModal, setTimeGridRecurrenceModal ] = useState<TimeGridRecurrenceItem | undefined>( undefined );
  const [ timegridRecurrenceModalUsersId, setTimeGridRecurrenceModalUsersId ] = useState<string[] | undefined>( undefined );
  const [ timegridRecurrenceModalFixed, setTimeGridRecurrenceModalFixed ] = useState<boolean>( false );
  const [ timegridRecurrenceModalCanHaveRecurrence, setTimeGridRecurrenceModalCanHaveRecurrence ] = useState<boolean>( true );
  const [ selectedTimeGridId, setSelectedTimeGridId ] = useState<string>( );

  const [ resetUsersSelected, setResetUsersSelected ] = useState<boolean>( false );

  const [ currentDay, setCurrentDay ] = useState<dayjs.Dayjs>( dayjs().startOf( 'week' ) );

  useEffect( () => {
    fetchTimeGridRecurrence( currentDay );
  }, [ currentDay ] );

  const [ activeTab, setActiveTab ] = useState( 0 );
  const pages: TabItem[] = [
    { id: 'timegrids', name: t( 'translation:time_grid' ) },
    { id: 'allocation', name: t( 'translation:allocation' ) }
  ];

  const [ isWeekEnd, setIsWeekEnd ] = useState( DEFAULT_VALUES.SHOWWEEKEND == 1 );

  const fetchSectorData = async ( sectorId: string | undefined = selectedSectorId ) => {
    if ( sectorId && sectorId != undefined ) {
      try {
        const _settings = await SettingService.getSectorSettingsBySector( sectorId, token, dispatch );
        const _isWeekEnd = ( _settings?.showWeekend?.value ?? DEFAULT_VALUES.SHOWWEEKEND ) == 1;
        setIsWeekEnd( _isWeekEnd );

        const dateStart = currentDay.startOf( 'week' ).add( 1, 'day' ).toISOString();
        const dateEnd = currentDay.endOf( 'week' ).add( -( 1 + ( _isWeekEnd ? 0 : 2 ) ), 'day' ).toISOString();
        const [ _timegrids, _timegrisRecurrences, _users ] = await Promise.all( [
          TimeGridService.getTimeGridsBySectorId( sectorId, token, dispatch ),
          TimeGridRecurrenceService.getTimeGridRecurrencesBySectorIdAndDates( sectorId, dateStart, dateEnd, false, token, dispatch ),
          UserService.getUsersOfSector( sectorId, token, dispatch ),
        ] );

        _timegrids.sort( ( a, b ) => {
          return a.name < b.name ? -1 : 1;
        } );
        setTimeGrids( _timegrids );
        setUsers( _users );

        _timegrisRecurrences.sort( ( a, b ) => {
          return a.date < b.date ? 1 : -1;
        } ).sort( ( a, b ) => {
          return a.idUser < b.idUser ? 1 : -1;
        } );

        setTimeGridRecurrences( _timegrisRecurrences );
      }
      catch ( error ) {
        console.error( 'error fetch data', error );
      }
    }
  };

  const resetSelectedUsers = () => {
    setResetUsersSelected( !resetUsersSelected );
  };

  const openTimeGridRecurrenceModal = ( timeGridRecurrence: TimeGridRecurrenceItem, usersId?: string[] ) => {
    if ( timegrids.length > 0 ) {
      setTimeGridRecurrenceModalFixed( !dayjs( timeGridRecurrence.date ).isAfter( dayjs().startOf( 'day' ) ) );
      setTimeGridRecurrenceModal( timeGridRecurrence );
      if ( usersId ) {
        // if date in past, fixed modal
        setTimeGridRecurrenceModalCanHaveRecurrence( true );
        setTimeGridRecurrenceModalUsersId( usersId );
      }
      else {
        // if there is already a timegrid on this date and user, disable the recurrence
        setTimeGridRecurrenceModalCanHaveRecurrence( !timegridRecurrences.some( tgr => { return tgr.date == timeGridRecurrence.date && tgr.idUser == timeGridRecurrence.idUser; } ) );
        setTimeGridRecurrenceModalUsersId( undefined );
      }
    }
    else {
      toastService.showWarning( t( 'translation:no_time_grid' ) );
    }
  };

  const fetchTimeGrid = async () => {
    if ( selectedSectorId ) {
      const _timegrids = await TimeGridService.getTimeGridsBySectorId( selectedSectorId, token, dispatch );
      if ( _timegrids ) {
        _timegrids.sort( ( a, b ) => {
          return a.name < b.name ? -1 : 1;
        } );
        setTimeGrids( _timegrids );
        fetchTimeGridRecurrence( currentDay );
      }
    }
  };

  const fetchTimeGridRecurrence = async ( day: dayjs.Dayjs ) => {
    if ( selectedSectorId ) {
      const dateStart = day.startOf( 'week' ).add( 1, 'day' ).toISOString();
      const dateEnd = day.endOf( 'week' ).add( -( 1 + ( isWeekEnd ? 0 : 2 ) ), 'day' ).toISOString();
      const _timegrisRecurrences = await TimeGridRecurrenceService.getTimeGridRecurrencesBySectorIdAndDates( selectedSectorId, dateStart, dateEnd, false, token, dispatch ) ?? [];
      setTimeGridRecurrences( _timegrisRecurrences );
    }
  };

  useEffect( () => {
    const init = () => {
      setIsFirstRender( false );
    };
    init();
  }, [] );

  useEffect( () => {
    if ( !isFirstRender && selectedSectorId != null ) {
      fetchSectorData( selectedSectorId );
    }
  }, [ selectedSectorId, token, isFirstRender ] );

  const onSectorsChange = ( sectorId: string | undefined ) => {
    setSelectedSectorId( sectorId );
  };

  const handleTimeGridSave = async ( timegrid: TimeGrid ) => {
    if ( timegrid.id != null && timegrid.id != '' ) {
      await handleTimeGridUpdate( timegrid );
    }
    else {
      await handleTimeGridCreate( timegrid );
    }
  };

  const handleTimeGridUpdate = async ( timegrid: TimeGrid ) => {
    try {
      const newTimegridId = await TimeGridService.putTimeGrid(
        timegrid.id,
        {
          name: timegrid.name,
          timeStart: timegrid.timeStart,
          timeEnd: timegrid.timeEnd,
          idSector: selectedSectorId
        } as ModifyTimeGrid, token, dispatch
      );

      await TimeGridService.deleteTimegridBreaktimes( newTimegridId.id, token, dispatch );
      await Promise.all(
        timegrid.breaktimes.map( br => {
          return BreaktimeService.postBreaktime( {
            idTimeGrid: newTimegridId.id,
            timeStart: br.timeStart,
            timeEnd: br.timeEnd
          } as PostBreaktime, token, dispatch );
        } )
      );
      await fetchTimeGrid( );
    }
    catch ( err ) {
      logger.error( 'Error while posting trimegrid or breaktime: ', err );
    }
  };

  const handleTimeGridCreate = async ( timegrid: TimeGrid ) => {
    try {
      const timegridId = ( await TimeGridService.postTimeGrid( {
        name: timegrid.name,
        timeStart: timegrid.timeStart,
        timeEnd: timegrid.timeEnd,
        idSector: selectedSectorId
      } as PostTimeGrid, token, dispatch ) ).id;

      if ( ( timegrid.breaktimes ?? [] ).length > 0 ) {
        await Promise.all(
          timegrid.breaktimes.map( br => {
            return BreaktimeService.postBreaktime( {
              idTimeGrid: timegridId,
              timeStart: br.timeStart,
              timeEnd: br.timeEnd
            } as PostBreaktime, token, dispatch );
          } )
        );
      }
      await fetchTimeGrid();
    }
    catch ( err ) {
      logger.error( 'Error while posting trimegrid or breaktime: ', err );
    }
  };

  const handleTimeGridDelete = async ( timegridId: string | undefined ) => {
    if ( timegridId || selectedTimeGridId ) {
      try {
        await TimeGridService.deleteTimeGrid( ( timegridId ?? selectedTimeGridId )!, token, dispatch );
        toastService.showSuccess( 'translation:timegrid_deleted' );
      } catch ( error: unknown ) {
        let errorHandled = false;

        if ( error && typeof error === 'object' && 'response' in error ) {
          const axiosError = error as { response?: { data?: { err?: number } } };
          const errorCode = axiosError.response?.data?.err;

          if ( errorCode === 2036 ) {
            toastService.showError( 'translation:errors.timegrid_already_used' );
            errorHandled = true;
          }
        }

        if ( !errorHandled ) {
          const errorMessage = String( error );
          if ( errorMessage.includes( 'ER_ROW_IS_REFERENCED_2' ) ||
               errorMessage.includes( 'foreign key constraint fails' ) ||
               errorMessage.includes( 'FOREIGN KEY constraint failed' ) ||
               errorMessage.includes( 'cannot delete or update a parent row' ) ||
               errorMessage.includes( 'ForeignKeyConstraintError' ) ||
               errorMessage.includes( 'it is saved by existing tasks' ) ||
               errorMessage.includes( 'TimeGrid is already being used' ) ) {
            toastService.showError( 'translation:errors.timegrid_already_used' );
          } else {
            toastService.showError( 'translation:errors.delete_error_generic' );
          }
        }
      }
    }
    await fetchTimeGrid();
    setIsDeleteTimeGridModalOpen( undefined );
    setSelectedTimeGridId( undefined );
  };

  return (
    <Page
      title={t( 'translation:timegrid' )}
      isAdminRequired={ true }
      onSectorChange={onSectorsChange}
    >
      <>
        <Tabs items={pages} activeItem={activeTab} onItemClick={setActiveTab} />
        <div className={ activeTab == 0 ? '' : 'hidden'}>
          <GroupSetting title={ t( 'translation:time_grids' )}>
            {[
              <div className='grid grid-cols-1 2xl:grid-cols-2 gap-2' key={'breaktime_grid'}>
                {
                  timegrids.filter( tg => { return !tg.disabled; } ).map( ( timegrid, index ) => {
                    return (
                      <TimeGridTile
                        onSave={ handleTimeGridSave }
                        onDelete={ () => { setIsDeleteTimeGridModalOpen( timegrid ); } }

                        sectorId={ selectedSectorId ?? ''}
                        timegrid={ timegrid }
                        disable={ timegrid.disabled }
                        key={ 'timegrid_' + ( timegrid.id ?? '' ) + '_' + index }
                      />
                    );
                  } )
                }
                <div className='w-full h-full flex justify-center items-center place-content'>
                  <div className={'flex right-0 place-content-end'}>
                    <button
                      type='button'
                      onClick={ () => { setTimeGrids( [ ...timegrids, {
                        id: '',
                        name: '',
                        idSector: selectedSectorId ?? '',
                        timeStart: '',
                        timeEnd: '',
                        disabled: false,
                        breaktimes: []
                      } ] ); } }
                      className='form-button bg-green-600 hover:bg-green-700 w-fit h-fit m-1 right-0 z-10'
                      id='timegrid_add_button'
                    >
                      <PlusSVG width={20} stroke="#FFFFFF" />
                      <p className='ml-2'>{ t( 'translation:add_timegrid' ) }</p>
                    </button>
                  </div>
                </div>
              </div>
            ]}
          </GroupSetting>
        </div>

        <div className={ activeTab == 1 ? '' : 'hidden'}>
          <TimeGridCalendar
            sectorId={ selectedSectorId ?? ''}
            timegridRecurrences={ timegridRecurrences }
            users={users}
            onTimeGridClick={ openTimeGridRecurrenceModal }
            isWeekEnd={isWeekEnd}
            currentDay={ currentDay }
            setCurrentDay={setCurrentDay}
            resetUsersSelected={resetUsersSelected}
          />

          {
            timegridRecurrenceModal &&
            ( users.some( u => { return u.id == timegridRecurrenceModal.idUser; } ) ||
            timegridRecurrenceModalUsersId?.every( uid => { return users.some( us => { return us.id == uid; } ); } ) ) &&
            timegrids.length > 0 &&
            <TimeGridModal
              sectorId={ selectedSectorId ?? '' }
              timeGridRecurrence={timegridRecurrenceModal}
              timegrids={timegrids}
              onClose={ () => {
                setTimeGridRecurrenceModal( undefined );
                setTimeGridRecurrenceModalUsersId( undefined );
                setTimeGridRecurrenceModalFixed( false );
                fetchTimeGridRecurrence( currentDay );
                resetSelectedUsers();
              } }
              usersId={ timegridRecurrenceModalUsersId }
              isWeekEnd={ isWeekEnd }
              disable={timegridRecurrenceModalFixed}
              canHaveRecurrence={timegridRecurrenceModalCanHaveRecurrence}
            />
          }
        </div>

        <ConfirmationModal
          isOpen={ isDeleteTimeGridModalOpen != null }
          onConfirm={ () => {
            handleTimeGridDelete( isDeleteTimeGridModalOpen?.id ?? '' );
            setIsDeleteTimeGridModalOpen( undefined );
          } }
          onCancel={ () => { setIsDeleteTimeGridModalOpen( undefined ); } }
          onClose={ () => { setIsDeleteTimeGridModalOpen( undefined ); } }
          title={ t( 'translation:delete_time_grid' ) }
          description={ t( 'translation:are_you_sure_that_you_want_to_delete_this_time_grid_it_is_irreversible' ) }
        />

        <ConfirmationModal
          isOpen={ isDeleteTimeGridRecurrenceModalOpen != null }
          onConfirm={ () => { setIsDeleteTimeGridRecurrenceModalOpen( undefined ); } }
          onCancel={ () => { setIsDeleteTimeGridRecurrenceModalOpen( undefined ); } }
          onClose={ () => { setIsDeleteTimeGridRecurrenceModalOpen( undefined ); } }
          title={ t( 'translation:delete_time_grid_recurrence' ) }
          description={ t( 'translation:are_you_sure_that_you_want_to_delete_this_time_grid_recurrence_it_is_irreversible' ) }
        />
      </>
    </Page>
  );
}

export default TimeGridView;